import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../components/ui/button'
import Loader from '../components/ui/loader.jsx'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'
import { Checkbox } from '../components/ui/checkbox'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import { PasswordInput } from '../components/ui/password-input'
import PasswordCriteriaValidator from '../components/PasswordCriteriaValidator'

const CreateAccountForm = () => {
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const { tenant } = useTenant()
  const { t, languageContent, languageId } = useLanguage()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    termsAccepted: false,
    privacyAccepted: false,
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  const currentLanguage = languageId === 2 ? 'ja' : 'en'

  // Keep track of previous language to detect changes
  const prevLanguageIdRef = useRef(languageId)

  useEffect(() => {
    // Check if language has actually changed
    if (prevLanguageIdRef.current !== languageId) {
      // Clear all error messages when language changes
      setErrors({})
      prevLanguageIdRef.current = languageId
    }
  }, [languageId])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
    // Clear HTML5 validation message
    e.target.setCustomValidity('')
  }

  const handlePasswordFocus = () => {
    setIsPasswordFocused(true)
  }

  const handlePasswordBlur = () => {
    setIsPasswordFocused(false)
  }

  // Intercept HTML5 validation and show translated errors
  const handleInvalid = (e) => {
    e.preventDefault()
    const { name, validity, value } = e.target
    const newErrors = { ...errors }

    if (name === 'email') {
      if (validity.valueMissing) {
        newErrors.email = t('signup.errors.emailRequired')
      } else if (validity.typeMismatch || validity.patternMismatch) {
        // Always use our custom validation message for email
        newErrors.email = t('signup.errors.emailInvalid')
      }
    } else if (name === 'password') {
      if (validity.valueMissing) {
        newErrors.password = t('signup.errors.passwordRequired')
      } else {
        // Run our custom password validation
        const passwordValidation = Validator.isValidPassword(value)
        if (!passwordValidation.valid) {
          newErrors.password = t(
            `signup.errors.${passwordValidation.errorCode}`,
          )
        } else {
          delete newErrors.password
        }
      }
    } else if (name === 'passwordConfirm') {
      if (validity.valueMissing) {
        newErrors.passwordConfirm = t('signup.errors.passwordConfirmRequired')
      } else if (formData.password !== value) {
        newErrors.passwordConfirm = t('signup.errors.passwordMismatch')
      } else {
        delete newErrors.passwordConfirm
      }
    }

    setErrors(newErrors)
    // Set custom validity to prevent browser's default message
    e.target.setCustomValidity(' ')
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate email (strict - no + sign for registration)
    if (!formData.email) {
      newErrors.email = t('signup.errors.emailRequired')
    } else if (!Validator.isValidEmailForRegistration(formData.email)) {
      newErrors.email = t('signup.errors.emailInvalid')
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = t('signup.errors.passwordRequired')
    } else {
      const passwordValidation = Validator.isValidPassword(formData.password)
      if (!passwordValidation.valid) {
        newErrors.password = t(`signup.errors.${passwordValidation.errorCode}`)
      }
    }

    // Validate password confirmation
    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = t('signup.errors.passwordConfirmRequired')
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = t('signup.errors.passwordMismatch')
    }

    // Validate terms acceptance
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = t('signup.errors.termsRequired')
    }

    // Validate privacy acceptance
    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = t('signup.errors.privacyRequired')
    }

    // // Validate captcha
    // if (!formData.captchaVerified) {
    //   newErrors.captchaVerified = t('signup.errors.captchaRequired')
    // }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    // Track sign-up CTA click for GTM
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'cta_click', ctaName: 'sign_up' })
    }

    try {
      // Execute reCAPTCHA v3
      let captchatoken = null
      if (executeRecaptcha) {
        try {
          captchatoken = await executeRecaptcha('signup')
        } catch (error) {
          console.error('reCAPTCHA execution failed:', error)
        }
      }

      // Get guest cookie preferences if they exist
      let guestCookiePreference = null
      try {
        const storageKey = `cookieConsent_${tenant?.tenantid}`
        const raw = localStorage.getItem(storageKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          // Simple structure: { essential, analytics, timestamp }
          guestCookiePreference = JSON.stringify({
            ...parsed,
            timestamp: new Date().toISOString(),
          })
        }
      } catch (e) {
        console.error('Failed to read guest cookie preferences', e)
      }

      // Call API to register user

      const response = await apiService.post('/api/kc/signup', {
        clientemail: formData.email,
        password: formData.password,
        termsAccepted: formData.termsAccepted,
        privacyAccepted: formData.privacyAccepted,
        clienttenantid: tenant?.tenantid || 1011,
        captchatoken,
        defaultlanguageid: languageId || null,
        displayemail: formData.email.split('@')[0] || '***',
        ...(guestCookiePreference && {
          cookiepreference: guestCookiePreference,
        }),
      })

      // Registration successful
      //console.log('Registration successful:', response)

      // Navigate to verify email page with tenant in URL
      // Pass email in both state and URL params for redundancy
      const tenantUrlName = tenant?.tenanturlname || tenanturlname
      const emailParam = encodeURIComponent(formData.email)
      navigate(`/${tenantUrlName}/verify-email?email=${emailParam}`, {
        state: { email: formData.email },
      })
    } catch (error) {
      console.error('Registration error:', error)
      console.error('Error status:', error.status)
      console.error('Error data:', error?.data)

      // Handle API errors - apiService.handleError transforms to { message, status, data, isRateLimit }
      if (error.status === 405) {
        setErrors({
          submit: 'API endpoint not available. Please contact support.',
        })
      } else if (error?.data?.code === 'OWER0005') {
        setErrors({ submit: t('signup.errors.restrictedEmail') })
      } else if (error?.data?.message) {
        setErrors({ submit: error.data.message })
      } else if (error?.status === 409) {
        setErrors({ email: t('signup.errors.emailExists') })
      } else if (
        error.isRateLimit ||
        error.message === 'Rate limit exceeded. Please try again later.'
      ) {
        setErrors({ submit: t('signup.errors.rateLimitExceeded') })
      } else {
        setErrors({ submit: t('signup.errors.registrationFailed') })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && <Loader fullscreen label={t('signup.loading')} size="xl" />}

      <div className="create-account-page">
        {/* BANNER - START */}
        <div className="main-banner w-full max-w-[648px] mx-auto">
          <div className="w-full aspect-[648/381] overflow-hidden">
            {/* Mobile banner */}
            <img
              src={
                tenant?.tenantcontentconfig?.images?.signup
                  ? `${process.env.REACT_APP_CLOUDFRONT}tenantbannerimages/${tenant?.tenanturlname}/mob_${tenant.tenantcontentconfig.images.signup}`
                  : require('../assets/images/backgrounds/main-banner.png')
              }
              alt={t('common.bannerAlt') || 'Banner'}
              className="block md:hidden w-full h-full object-contain border border-gray-300 rounded-md"
              loading="lazy"
              onError={(e) => {
                e.target.src = require('../assets/images/backgrounds/main-banner.png')
              }}
            />
            {/* Desktop banner */}
            <img
              src={
                tenant?.tenantcontentconfig?.images?.signup
                  ? `${process.env.REACT_APP_CLOUDFRONT}tenantbannerimages/${tenant?.tenanturlname}/web_${tenant.tenantcontentconfig.images.signup}`
                  : require('../assets/images/backgrounds/main-banner.png')
              }
              alt={t('common.bannerAlt') || 'Banner'}
              className="hidden md:block w-full h-full object-contain border border-gray-300 rounded-md"
              loading="lazy"
              onError={(e) => {
                e.target.src = require('../assets/images/backgrounds/main-banner.png')
              }}
            />
          </div>
        </div>
        {/* BANNER - END */}

        <div className="px-6 mt-6">
          {/* Create Account - START w-full px-6 md:max-w-[600px] md:mx-auto */}
          <form
            className="form-container"
            onSubmit={handleSubmit}
            aria-label="Sign up form"
          >
            <div className="text-center mb-4">
              <h1 className="text-base font-extrabold text-theme-secondary">
                {t('signup.title')}
              </h1>
              <p className="text-xs text-theme-secondary mt-2">
                {t('signup.subtitle')}
              </p>
              <p className="text-xs text-theme-secondary mt-1">
                {t('signup.alreadyHaveAccount2.text1')}
                <a
                  href={`/${tenanturlname}/signin`}
                  className="text-xs text-theme-primary hover:text-theme-primary/80 inline-block"
                >
                  {t('signup.alreadyHaveAccount2.text2')}
                </a>
              </p>
            </div>

            {/* Display general error */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-500">{errors.submit}</p>
              </div>
            )}

            <div className="form-body">
              <div className="w-full md:max-w-md md:mx-auto space-y-4">
                <div className="grid gap-1">
                  <label
                    htmlFor="email"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('signup.emailLabel')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onInvalid={handleInvalid}
                    required
                    pattern="^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('signup.emailPlaceholder')}
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p
                      id="email-error"
                      className="text-sm text-red-500 mt-1 text-left"
                    >
                      <div className="flex items-start justify-start gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 mt-2"></span>
                        <span>{errors.email}</span>
                      </div>
                    </p>
                  )}
                </div>

                <div className="grid gap-1">
                  <label
                    htmlFor="password"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('signup.passwordLabel')}
                  </label>
                  <PasswordInput
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onInvalid={handleInvalid}
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('signup.passwordPlaceholder')}
                    autoComplete="new-password"
                    aria-required="true"
                    aria-invalid={!!errors.password}
                    aria-describedby={
                      errors.password ? 'password-error' : undefined
                    }
                  />

                  <PasswordCriteriaValidator
                    password={formData.password}
                    language={currentLanguage}
                    isVisible={isPasswordFocused}
                  />

                  {errors.password && (
                    <p
                      id="password-error"
                      className="text-sm text-red-500 mt-1 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                        {errors.password}
                      </div>
                    </p>
                  )}
                </div>

                <div className="grid gap-1">
                  <label
                    htmlFor="passwordConfirm"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('signup.passwordConfirmLabel')}
                  </label>
                  <PasswordInput
                    id="passwordConfirm"
                    name="passwordConfirm"
                    value={formData.passwordConfirm}
                    onChange={handleInputChange}
                    onInvalid={handleInvalid}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.passwordConfirm
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('signup.passwordConfirmPlaceholder')}
                    autoComplete="new-password"
                    aria-required="true"
                    aria-invalid={!!errors.passwordConfirm}
                    aria-describedby={
                      errors.passwordConfirm
                        ? 'passwordConfirm-error'
                        : undefined
                    }
                  />
                  {errors.passwordConfirm && (
                    <p
                      id="passwordConfirm-error"
                      className="text-sm text-red-500 mt-1 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                        {errors.passwordConfirm}
                      </div>
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  <div className="inline-flex items-start gap-3 w-full text-left">
                    <Checkbox
                      id="terms"
                      name="termsAccepted"
                      checked={formData.termsAccepted}
                      onCheckedChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          termsAccepted: checked,
                        }))
                        if (errors.termsAccepted) {
                          setErrors((prev) => ({ ...prev, termsAccepted: '' }))
                        }
                      }}
                    />

                    <span className="text-sm font-normal text-theme-secondary block">
                      {t('signup.terms.beforeLink')}
                      <a
                        href={`/${tenanturlname}${t('signup.terms.linkUrl')}`}
                        className="text-theme-primary hover:text-theme-primary/80 underline"
                      >
                        {t('signup.terms.linkText')}
                      </a>
                      {t('signup.terms.afterLink')}
                    </span>
                  </div>
                  {errors.termsAccepted && (
                    <p className="text-sm text-red-500 ml-6 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                        {errors.termsAccepted}
                      </div>
                    </p>
                  )}

                  <div className="inline-flex items-start gap-3 w-full text-left">
                    <Checkbox
                      id="privacy"
                      name="privacyAccepted"
                      checked={formData.privacyAccepted}
                      onCheckedChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          privacyAccepted: checked,
                        }))
                        if (errors.privacyAccepted) {
                          setErrors((prev) => ({
                            ...prev,
                            privacyAccepted: '',
                          }))
                        }
                      }}
                    />

                    <span className="text-sm font-normal text-theme-secondary block">
                      {t('signup.privacy.beforeLink')}
                      <a
                        href={
                          t('signup.privacy.isExternal') === true
                            ? t('signup.privacy.linkUrl')
                            : `/${tenanturlname}${t('signup.privacy.linkUrl')}`
                        }
                        className="text-theme-primary hover:text-theme-primary/80 underline"
                        target={
                          t('signup.privacy.isExternal') === 'true'
                            ? '_blank'
                            : '_self'
                        }
                        rel={
                          t('signup.privacy.isExternal') === 'true'
                            ? 'noopener noreferrer'
                            : ''
                        }
                      >
                        {t('signup.privacy.linkText')}
                      </a>
                      {t('signup.privacy.afterLink')}
                    </span>
                  </div>
                  {errors.privacyAccepted && (
                    <p className="text-sm text-red-500 ml-6 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                        {errors.privacyAccepted}
                      </div>
                    </p>
                  )}
                </div>

                {/* <div className="mt-2">
                  <div
                    className={`flex items-center justify-between py-6 px-3 rounded-md border ${
                      errors.captchaVerified
                        ? 'border-red-500'
                        : 'border-gray-200'
                    } bg-gray-50`}
                  >
                    <label className="inline-flex items-center gap-3 cursor-pointer w-full text-left">
                      <input
                        type="checkbox"
                        name="captchaVerified"
                        checked={formData.captchaVerified}
                        onChange={handleInputChange}
                        aria-label="I am not a robot"
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {t('signup.captchaText')}
                      </span>
                    </label>
                    <div className="text-xs text-gray-400">
                      {t('signup.captchaLabel')}
                    </div>
                  </div>
                  {errors.captchaVerified && (
                    <p className="text-sm text-red-500 mt-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                        {errors.captchaVerified}
                      </div>
                    </p>
                  )}
                </div> */}

                <Button
                  id="signup-btn"
                  variant="primary"
                  size="lg"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {t('signup.submitButton')}
                </Button>
              </div>

              <div className="text-xs text-gray-500 mt-4 space-y-1">
                <p className="text-left md:text-center text-xs text-theme-secondary font-normal">
                  {t('signup.emailNotice')}
                </p>
                <p className="text-left md:text-center text-xs text-theme-secondary font-normal">
                  {t('signup.verificationNotice')}
                </p>
                <p className="text-left md:text-center text-xs text-theme-secondary font-normal">
                  {t('signup.alreadyRegisteredNotice.text1')}{' '}
                  <a
                    href={`/${tenanturlname}/signin`}
                    className="text-theme-primary hover:text-theme-primary/80 underline"
                  >
                    {t('signup.alreadyRegisteredNotice.text2')}
                  </a>{' '}
                  {t('signup.alreadyRegisteredNotice.text3')}
                </p>
                <p className="text-left md:text-center text-xs text-theme-secondary font-normal">
                  {t('signup.deliveryNotice')}
                </p>
              </div>

              <div className="mt-3 w-full md:max-w-md md:mx-auto">
                <Button
                  id="view-offers-guest"
                  variant="secondary"
                  size="lg"
                  type="button"
                  onClick={() => navigate(`/${tenanturlname}`)}
                >
                  {t('signup.viewAsGuestButton')}
                </Button>
              </div>
            </div>
          </form>
          {/* Create Account - END */}
        </div>
      </div>
    </>
  )
}

const CreateAccount = () => {
  const { tenant } = useTenant()
  const [recaptchaSiteKey, setRecaptchaSiteKey] = React.useState(null)

  React.useEffect(() => {
    // Wait for tenant to load, then set the recaptcha key
    const key =
      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
      tenant?.tenantmasterconfig?.recaptcha?.siteKey
    setRecaptchaSiteKey(key)
  }, [tenant])

  // Don't render until we have the key
  if (!recaptchaSiteKey) {
    return null
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      language="en"
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
    >
      <CreateAccountForm />
    </GoogleReCaptchaProvider>
  )
}

export default CreateAccount
